import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import LandingPage from './components/LandingPage';
import HomePage from './components/HomePage';
import TrendingQuestions from './components/TrendingQuestions';
import UserProfile from './components/UserProfile';
import QuestionDetail from './components/QuestionDetail';
import AskQuestion from './components/AskQuestion';
import SmartSearch from './components/SmartSearch';
import Login from './components/Login';
import Register from './components/Register';
import RealtimeNotifications from './components/RealtimeNotifications';
import config from './config';
import './App.css';
import './animations.css';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('React Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="text-6xl mb-4">😵</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Oops! Something went wrong</h1>
            <p className="text-gray-600 mb-6">We're sorry, but there was an error loading the page.</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Context for global state
const AppContext = createContext();

// Custom hook to use the context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// API base URL with fallback
const API_BASE_URL = `${config?.API_BASE_URL || 'https://stackit-backend.officialchiragp1605.workers.dev'}/api`;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const savedUser = localStorage.getItem('stackit_user');
    const savedToken = localStorage.getItem('stackit_token');
    
    if (savedUser && savedToken) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      
      if (data.success) {
        setUser(data.user);
        localStorage.setItem('stackit_user', JSON.stringify(data.user));
        localStorage.setItem('stackit_token', data.token);
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      return { success: false, message: 'Network error' };
    }
  };

  const register = async (username, email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, email, password }),
      });

      const data = await response.json();
      
      if (data.success) {
        setUser(data.user);
        localStorage.setItem('stackit_user', JSON.stringify(data.user));
        localStorage.setItem('stackit_token', data.token);
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      return { success: false, message: 'Network error' };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('stackit_user');
    localStorage.removeItem('stackit_token');
  };

  const contextValue = {
    user,
    login,
    register,
    logout,
    API_BASE_URL,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-border text-blue-600" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-4 text-gray-600 text-lg">Loading StackIt...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <AppContext.Provider value={contextValue}>
        <Router>
          <div className="min-h-screen bg-gray-50">
            <Routes>
              {/* Landing page route */}
              <Route path="/" element={
                <ErrorBoundary>
                  <Header />
                  <LandingPage />
                </ErrorBoundary>
              } />
              
              {/* Questions page route */}
              <Route path="/questions" element={
                <ErrorBoundary>
                  <Header />
                  <main className="container mx-auto px-4 py-8">
                    <HomePage />
                  </main>
                </ErrorBoundary>
              } />
              
              {/* Trending questions page route */}
              <Route path="/trending" element={
                <ErrorBoundary>
                  <Header />
                  <main className="container mx-auto px-4 py-8">
                    <TrendingQuestions />
                  </main>
                </ErrorBoundary>
              } />
              
              {/* User profile routes */}
              <Route path="/profile" element={
                user ? (
                  <ErrorBoundary>
                    <Header />
                    <main className="container mx-auto px-4 py-8">
                      <UserProfile />
                    </main>
                  </ErrorBoundary>
                ) : (
                  <Navigate to="/login" replace />
                )
              } />
              <Route path="/profile/:userId" element={
                <ErrorBoundary>
                  <Header />
                  <main className="container mx-auto px-4 py-8">
                    <UserProfile />
                  </main>
                </ErrorBoundary>
              } />
              
              {/* Question detail route */}
              <Route path="/questions/:id" element={
                <ErrorBoundary>
                  <Header />
                  <main className="container mx-auto px-4 py-8">
                    <QuestionDetail />
                  </main>
                </ErrorBoundary>
              } />
              
              {/* AI Smart Search route */}
              <Route path="/search" element={
                <ErrorBoundary>
                  <Header />
                  <main className="container mx-auto px-4 py-8">
                    <SmartSearch />
                  </main>
                </ErrorBoundary>
              } />
              
              {/* Ask question route */}
              <Route path="/ask" element={
                user ? (
                  <ErrorBoundary>
                    <Header />
                    <main className="container mx-auto px-4 py-8">
                      <AskQuestion />
                    </main>
                  </ErrorBoundary>
                ) : (
                  <Navigate to="/login" replace />
                )
              } />
              
              {/* Auth routes */}
              <Route path="/login" element={
                !user ? (
                  <ErrorBoundary>
                    <Header />
                    <main className="container mx-auto px-4 py-8">
                      <Login />
                    </main>
                  </ErrorBoundary>
                ) : (
                  <Navigate to="/" replace />
                )
              } />
              
              <Route path="/register" element={
                !user ? (
                  <ErrorBoundary>
                    <Header />
                    <main className="container mx-auto px-4 py-8">
                      <Register />
                    </main>
                  </ErrorBoundary>
                ) : (
                  <Navigate to="/" replace />
                )
              } />
              
              {/* Redirect all other routes to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
          
          {/* Real-time notifications */}
          <RealtimeNotifications />
        </Router>
      </AppContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
